import React from 'react'
import styled from 'styled-components'
import { FaSearch } from 'react-icons/fa'
import moment from 'moment'
import { Tooltip } from 'react-tippy'

import { cn } from 'utils/classMerge'

import { palette, fonts, mediaQueries } from '../../styles/variables'
import arrowSort from '../../styles/assets/arrows/arrowSort.svg'
import filterIcon from '../../styles/assets/tableIcons/filter.svg'
import filterSelectedIcon from '../../styles/assets/tableIcons/filter_white.svg'
import reloadIcon from '../../styles/assets/tableIcons/reload-orange.svg'
import reloadGrayIcon from '../../styles/assets/tableIcons/reload-gray.svg'
import checkmarkIcon from '../../styles/assets/tableIcons/checkmark.svg'
import Icon, { SpiningIcon } from '../icon/Icon'
import { LoadingResponsive, LoadingWheel } from '../loading/Loading'
import { getOS } from '../../utils/functions'
import { Text } from '../topography'
import { translate } from '../../utils/translation'
import CustomTooltip from '../tooltip/Tooltip'

const DefaultButton = styled.button((props) => ({
  display: 'inline-flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  padding: '18px',
  width: props.width || 'auto',
  border: `1px solid ${props.color || palette.orange}`,
  borderRadius: '2px',
  backgroundColor: palette.transparent,
  color: props.color || palette.orange,
  fontFamily: fonts.primaryMedium,
  fontSize: '12px',
  letterSpacing: '2px',
  textTransform: 'uppercase',
  cursor: props.disabled ? 'initial' : 'pointer',

  ':hover': props.static
    ? {}
    : {
        backgroundColor: props.color || palette.orange,
        color: 'white',
      },

  [mediaQueries.mobile]: {
    ...(props?.mobileStyles || {}),
  },
}))

const OutlineButton = styled(DefaultButton)((props) => ({
  color: props.disabled ? palette.lightGray : props.color || palette.orange,
  pointerEvents: props.disabled ? 'none' : 'all',
  border: `1px solid ${
    props.disabled ? palette.lightGray : props.color || palette.orange
  }`,
  backgroundColor: palette.white,
  [mediaQueries.mobile]: {
    width: props.mobileWidth || props.width || 'auto',
  },
  ...props.styles,
}))

const getViewAllButtonColor = (disabled, active) => {
  if (disabled) return palette.lightGray70
  if (active) return palette.white
  return palette.orange
}

const ViewAllButton = styled.button(({ disabled, active }) => {
  const borderColor = disabled ? palette.lightGray70 : palette.orange
  const color = getViewAllButtonColor(disabled, active)
  const backgroundColor = active ? palette.orange : palette.transparent

  return {
    border: `1px solid ${borderColor}`,
    borderRadius: '2px',
    height: '25px',
    lineHeight: getOS() === 'Win' ? '1em' : '25px',
    minWidth: 'fit-content',
    padding: '0 15px 0 10px',
    color,
    backgroundColor,
    cursor: disabled ? 'not-allowed' : 'pointer',
    ':after': {
      content: `""`,
      display: 'inline-block',
      width: '5px',
      height: '5px',
      border: '0',
      padding: '0',
      backgroundColor: 'transparent',
      borderLeft: `1px solid ${color}`,
      borderBottom: `1px solid ${color}`,
      transform: active
        ? 'translate(5px, 0px) rotate(-225deg)'
        : 'translate(5px, -3px) rotate(-45deg)',
    },
  }
})

// result
const PrimaryButtonElement = styled.button((props) => {
  const getBorderColor = ({ disabled, theme }) => {
    if (disabled) return palette.gray
    if (theme === 'blue') return palette[theme]
    return palette.orange
  }

  const getBackgroundColor = ({ disabled, theme }) => {
    if (disabled) return palette.gray
    if (theme && palette[theme]) return palette[theme]
    return palette.white
  }

  const getColor = ({ disabled, theme }) => {
    if (disabled) return palette.white
    if (theme === 'orange' || theme === 'blue') return palette.white
    return palette.orange
  }

  return {
    border: `1px solid ${getBorderColor(props)}`,
    borderRadius: '2px',
    pointerEvents: props.disabled ? 'none' : 'all',
    backgroundColor: getBackgroundColor(props),
    fontSize: props.fontSize || '12px',
    width: props.width || 'fit-content',
    minWidth: '120px',
    height: props.height || '50px',
    lineHeight: props.height || '50px',
    cursor: 'pointer',
    color: getColor(props),
    fontFamily: fonts.primaryMedium,
    textTransform: props.textTransform || 'uppercase',
    padding: '0 20px',
    letterSpacing: props.letterSpacing || '2px',
    '& + button': {
      marginLeft: '16px',
    },
    [mediaQueries.mobile]: {
      width: props.mobileWidth || props.width || 'fit-content',
      ...(props?.mobileStyles || {}),
    },
    [mediaQueries.smallMobile]: {
      minWidth: 'auto',
    },
  }
})

const PrimaryButton = ({ testId = '', ...props }) => (
  <PrimaryButtonElement data-testid={testId} {...props}>
    {props.isLoading ? (
      <LoadingResponsive
        size="30px"
        height="42px"
        offsetY="5%"
        color={
          props.theme === 'orange' || props.theme === 'blue'
            ? palette.white
            : palette.orange
        }
      />
    ) : (
      props.tag
    )}
  </PrimaryButtonElement>
)

// footer
const FooterButtonElement = styled.button((props) => {
  return {
    backgroundColor: props?.backgroundColor || palette.orange,
    border: props?.border || '1px solid white',
    fontSize: props?.fontSize || '12px',
    color: props?.color || 'white',
    height: props?.height || '40px',
    maxWidth: props?.maxWidth || '207px',
    padding: props?.padding || '0 20px',
    borderRadius: '2px',
    fontWeight: props?.fontWeight || 'bold',
    margin: props?.margin || '0 0 0 20px',
    letterSpacing: '1px',
    width: props?.width || 'initial',
    [mediaQueries.mobile]: {
      margin: `${props.mobileMargin || '0 0 0 20px'} !important`,
      fontSize: `${props.mobileFontSize || '12px'} !important`,
      width: props.mobileWidth || 'inherit',
      height: props.mobileHeight || '60px',
      maxWidth: props.mobileWidth ? 'none' : '207px',
      ...props.mobileStyles,
    },
  }
})

const FooterButton = ({
  tag,
  id,
  onClick,
  dataTip,
  dataEvent,
  dataEventOff,
  style,
  mobileMargin,
  mobileFontSize,
  mobileWidth,
  mobileHeight,
  mobileStyles,
  testId,
  icon = null,
}) => (
  <FooterButtonElement
    tag={tag}
    id={id || ''}
    onClick={onClick}
    data-tip={dataTip}
    data-event={dataEvent}
    data-event-off={dataEventOff}
    style={style}
    mobileMargin={mobileMargin}
    mobileFontSize={mobileFontSize}
    mobileWidth={mobileWidth}
    mobileHeight={mobileHeight}
    mobileStyles={mobileStyles}
    data-testid={testId || ''}
  >
    {icon}
    {tag.toUpperCase()}
  </FooterButtonElement>
)

// tables
const TableButtonElement = styled(PrimaryButtonElement)(
  ({
    width = 'auto',
    activeColor = palette.orange,
    inactiveColor = palette.white,
    isLoading = false,
  }) => ({
    pointerEvents: isLoading ? 'none' : 'all',
    backgroundColor: inactiveColor,
    color: activeColor,
    width,
    minWidth: 'auto',
    height: '45px',
    lineHeight: '45px',
    ':hover': {
      backgroundColor: activeColor,
      color: inactiveColor,
    },
    [mediaQueries.mobile]: {
      fontFamily: fonts.primaryBold,
      fontWeight: 'bold',
      height: '45px',
      padding: '0 10px',
      margin: 0,
      lineHeight: '20px',
      '& + button': {
        marginLeft: '10px',
      },
      ':hover': {
        backgroundColor: inactiveColor,
        color: activeColor,
      },
    },
  }),
)

const TableButton = ({
  onClick,
  width,
  activeColor,
  inactiveColor,
  disabled,
  style,
  isLoading,
  tag,
  loadingText,
}) => (
  <TableButtonElement
    data-testid="table-button"
    onClick={onClick}
    width={width}
    activeColor={activeColor}
    inactiveColor={inactiveColor}
    disabled={disabled}
    style={style}
    isLoading={isLoading}
  >
    {isLoading !== undefined ? (
      <div>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '8px' }}>{loadingText}</span>
            <LoadingWheel width="16px" height="40px" />
          </div>
        ) : (
          tag.toUpperCase()
        )}
      </div>
    ) : (
      <div>{tag.toUpperCase()}</div>
    )}
  </TableButtonElement>
)

// sort
const SortButtonElement = styled.button({
  border: '0',
  backgroundColor: 'transparent',
  padding: 0,
  margin: 0,
})

const SortButton = ({ sort, styles, onClick }) => (
  <SortButtonElement
    style={{
      display: sort === 'TREND' ? 'none' : 'inline',
      height: '20px',
      ...styles,
    }}
    onClick={onClick}
  >
    <Icon src={arrowSort} width="20px" height="20px" alt="sort" />
  </SortButtonElement>
)

// search
const SearchButtonElement = styled.button(
  ({ disabled, useIcon, theme = 'orange', isHistoricalSearch }) => {
    const color = disabled ? palette.lightGray : palette[theme]
    if (useIcon) {
      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${color}`,
        backgroundColor: color,
        fontSize: '25px',
        borderRadius: '2px',
        width: '50px',
        cursor: 'pointer',
        color: palette.white,
        fontFamily: fonts.primaryMedium,
        letterSpacing: '2px',
        height: '40px',
        textTransform: 'uppercase',
        [mediaQueries.mobile]: {
          width: '100%',
          height: '40px',
          margin: '10px 0 0 0',
        },
        [mediaQueries.tablet]: {
          marginBottom: '10px',
        },
      }
    }
    return {
      border: `1px solid ${color}`,
      backgroundColor: color,
      fontSize: '12px',
      width: isHistoricalSearch ? '' : '162px',
      minWidth: 'fit-content',
      padding: '0 20px',
      cursor: 'pointer',
      color: palette.white,
      fontFamily: fonts.primaryMedium,
      letterSpacing: '2px',
      height: '40px',
      lineHeight: '40px',
      textTransform: 'uppercase',
      [mediaQueries.mobile]: {
        width: '100%',
        height: '40px',
        margin: '10px 0 0 0',
        textTransform: 'uppercase',
      },
      [mediaQueries.tablet]: {
        marginBottom: '10px',
      },
    }
  },
)

const SearchButton = ({
  testId,
  useIcon,
  onClick,
  disabled,
  style,
  theme,
  tag,
  isHistoricalSearch = false,
  wrapperClassName,
}) => (
  <SearchButtonElement
    data-testid={testId}
    useIcon={useIcon}
    onClick={onClick}
    disabled={disabled}
    style={style}
    theme={theme || 'orange'}
    className={cn(wrapperClassName, {
      'tw-rounded-md': isHistoricalSearch,
      'tw-rounded-sm': !isHistoricalSearch,
    })}
    isHistoricalSearch
  >
    {useIcon ? <FaSearch role="img" /> : tag}
  </SearchButtonElement>
)

const IconButtonContent = styled.div({
  padding: '3px',
  cursor: 'pointer',
})

const IconButton = ({ icon, iconWidth, iconHeight, ..._ }) => (
  <IconButtonContent role="button" {..._}>
    <Icon src={icon} width={iconWidth} height={iconHeight} />
  </IconButtonContent>
)

const IconButtonWithTooltip = ({ text, distance = 0, ..._ }) => (
  <CustomTooltip
    textStyle={{
      minWidth: 'fit-content',
      padding: '5.6px 0',
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    text={text}
    content={<IconButton {..._} />}
    place="top"
    offset={-100}
    distance={distance}
  />
)

// leftArrow
const DivLeftArrow = styled.div((props) => {
  if (props.collapse) {
    return {
      display: 'inline-block',
      width: '10px',
      height: '10px',
      border: '0',
      padding: '0',
      marginLeft: '-15px',
      backgroundColor: 'transparent',
      borderLeft: `2px solid ${palette.orange}`,
      borderTop: `2px solid ${palette.orange}`,
      transform: 'translate(5px, -3px) rotate(135deg)',
      '@media screen and (min-color-index:0) and(-webkit-min-device-pixel-ratio:0)':
        {
          marginLeft: '30px',
        },
      [mediaQueries.mobile]: {
        borderLeft: `2px solid ${palette.white}`,
        borderTop: `2px solid ${palette.white}`,
      },
    }
  }
  return {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    border: '0',
    padding: '0',
    backgroundColor: 'transparent',
    marginRight: '28px',
    borderLeft: `2px solid ${palette.orange}`,
    borderTop: `2px solid ${palette.orange}`,
    transform: 'translate(5px, -1px) rotate(-45deg)',
    [mediaQueries.tablet]: {
      margin: '0',
    },
    [mediaQueries.mobile]: {
      borderLeft: `2px solid ${palette.white}`,
      borderTop: `2px solid ${palette.white}`,
    },
  }
})

const MobileMenuButton = styled.div((props) => ({
  width: '30px',
  height: '30px',
  position: 'absolute',
  top: '15px',
  right: '20px',
  [mediaQueries.mobile]: {
    top: '17px',
    zIndex: 999,
  },
  ...(props.styles || {}),
}))

const LinkButton = styled.div((props) => ({
  display: 'inline-block',
  fontSize: '14px',
  color: props.disabled ? palette.lightGray50 : palette.orange,
  pointerEvents: props.disabled ? 'none' : 'all',
  cursor: 'pointer',
  letterSpacing: '0.05em',
}))

const MobileShowButton = styled.div({
  border: `1px solid ${palette.lightGray70}`,
  borderRadius: '2px',
  width: 'auto',
  color: palette.darkGray,
  backgroundColor: 'transparent',
  fontFamily: fonts.primaryMedium,
  fontSize: '10px',
  fontWeight: 500,
  textTransform: 'uppercase',
  padding: '10px 20px',
  textAlign: 'center',
})

const CloseButton = styled.div((props) => ({
  position: 'absolute',
  top: props.closeBtnClass || '10px',
  right: props.closeBtnClass || '20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: props.size ?? '24px',
  height: props.size ?? '24px',
  borderRadius: props.circled ? '12px' : 0,
  cursor: 'pointer',
  ':before': {
    content: `''`,
    borderLeft: `2px solid ${props.color || palette.pureBlack}`,
    height: props.size || '18px',
    transform: 'rotate(45deg)',
  },
  ':after': {
    content: `''`,
    borderLeft: `2px solid ${props.color || palette.pureBlack}`,
    height: props.size || '18px',
    transform: 'rotate(-45deg)',
    marginLeft: '-2px',
  },
  ...(props.styles || {}),
}))

const OrangeButton = styled.div(() => ({
  border: `1px solid ${palette.orange}`,
  borderRadius: '2px',
  backgroundColor: palette.orange,
  fontSize: '14px',
  height: '56px',
  padding: '0 30px',
  cursor: 'pointer',
  color: palette.white,
  fontFamily: fonts.primary,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  display: 'flex',
  alignItems: 'center',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.24)',
}))

const FrameFooterButtons = styled.div((props) => ({
  display: 'flex',
  marginTop: '20px',
  paddingBottom: '10px',
  justifyContent: 'flex-end',
  alignItems: 'center',
  '& > button': {
    flex: props.small ? '1 1 120px' : '1 1 207px',
    fontSize: '12px',
    margin: '0 0 0 10px',
  },
}))

const Filter = styled(DefaultButton)((props) => ({
  background: props.active ? palette.gray : palette.white,
  border: `1px solid ${palette.gray}`,
  height: '40px',
  borderRadius: '2px',
  fontSize: '14px',
}))

const Badge = styled.div(() => ({
  background: palette.orange,
  minWidth: '16px',
  height: '16px',
  borderRadius: '10px',
  color: palette.white,
  fontSize: '12px',
  fontFamily: fonts.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: '8px',
  padding: getOS() === 'Mac' ? '2px 0 0 2px' : '0 0 0 2px',
}))

const FilterButton = ({ active, onClick, style, title, badgeCount }) => {
  return (
    <Filter
      data-testid="filter-button"
      static
      active={active}
      color={active ? palette.white : palette.gray}
      onClick={onClick}
      style={style}
    >
      <Icon src={active ? filterSelectedIcon : filterIcon} margin="0 8px 0 0" />
      {title}
      {badgeCount > 0 && <Badge color={palette.orange}>{badgeCount}</Badge>}
    </Filter>
  )
}

const RefreshButtonWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-end',
  marginRight: '12px',
  flexWrap: 'wrap',
  [mediaQueries.mobile]: {
    marginTop: '8px',
  },
})

const RefreshButton = ({
  isLoading,
  lastUpdatedAt,
  title,
  loadingText,
  onRefresh,
}) => {
  const isUpToDate =
    moment.utc().diff(moment.utc(lastUpdatedAt, 'YYYY-MM-DD'), 'days') < 1
  const color = isLoading || isUpToDate ? palette.lightGray : palette.orange

  const renderLoadingOrUpToDate = () => {
    if (isLoading) {
      return (
        <Text fontSize="10px" lineHeight="14px" color={palette.gray}>
          {loadingText}
        </Text>
      )
    }
    if (isUpToDate) {
      return (
        <Text
          fontSize="10px"
          lineHeight="14px"
          color={palette.gray}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Icon src={checkmarkIcon} />
          {translate('up_to_date')}
        </Text>
      )
    }

    return null
  }

  return (
    <RefreshButtonWrapper>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isLoading ? (
          <SpiningIcon src={reloadIcon} margin="0 8px 0 0" size="20px" />
        ) : (
          <Icon
            src={isUpToDate ? reloadGrayIcon : reloadIcon}
            margin="0 8px 0 0"
            size="20px"
          />
        )}
        <Tooltip
          arrow
          position="top"
          theme="dark"
          popperOptions={{
            modifiers: {
              preventOverflow: {
                enabled: false,
              },
              hide: {
                enabled: false,
              },
            },
          }}
          html={
            <Text
              fontSize="12px"
              lineHeight="16px"
              color={palette.white}
              style={{ width: '200px' }}
              dangerouslySetInnerHTML={{
                __html: isUpToDate
                  ? translate('refresh_serp_data_disabled_tooltip', 24)
                  : translate('refresh_serp_data_tooltip'),
              }}
              boldColor={palette.white}
            />
          }
        >
          <Text
            lineHeight="16px"
            color={color}
            textDecoration="underline"
            style={{
              cursor: isUpToDate || isLoading ? 'not-allowed' : 'pointer',
            }}
            onClick={isUpToDate || isLoading ? () => ({}) : onRefresh}
          >
            {title}
          </Text>
        </Tooltip>
      </div>
      {renderLoadingOrUpToDate()}
    </RefreshButtonWrapper>
  )
}

const GenerateContentWithAIButton = styled(OutlineButton)((props) => ({
  width: 'fit-content',
  borderRadius: props.borderRadius || '2px',
  backgroundColor: props.bgColor || palette.white,
  padding: '4px 8px',
  fontSize: '14px',
  lineHeight: '16px',
  fontFamily: fonts.primary,
  textTransform: 'capitalize',
  letterSpacing: 'normal',
  whiteSpace: 'nowrap',
  ':hover': {
    backgroundColor: palette.orange,
    color: 'white',
    '& span': {
      color: 'white',
    },
  },
  [mediaQueries.smallDesktop]: {
    width: '180px',
    whiteSpace: 'wrap',
  },
}))

export {
  DefaultButton,
  PrimaryButtonElement,
  PrimaryButton,
  ViewAllButton,
  FooterButton,
  SearchButton,
  SortButton,
  DivLeftArrow,
  TableButton,
  MobileMenuButton,
  LinkButton,
  OutlineButton,
  IconButton,
  IconButtonWithTooltip,
  MobileShowButton,
  CloseButton,
  OrangeButton,
  FrameFooterButtons,
  FilterButton,
  RefreshButton,
  GenerateContentWithAIButton,
}
